import axios, { AxiosResponse } from 'axios';

import config from '../../../../config/config';
import { CreateMembershipModel } from '../../../../models/types/CreateMembershipModel';
import { IMembershipDates } from '../../../../models/MembershipDates';
import { IAddMembershipSummaryModel } from '../../../../models/interfaces/Membership/AddMembershipSummaryModel';
import {
  prepareQueryParams,
  QueryParamItem,
} from '../../../../utils/validations/queryParamsHelpers';
import { IPageListModel } from '../../../../models/PageListModel';
import { IMemberListItem } from '../../../../models/MemberListItem';
import { ICreateSecondaryMemberModel } from '../../../../models/interfaces/Membership/ICreateSecondaryMemberModel';
import { IAAMembershipModel } from '../../../../models/interfaces/Membership/AirAmbulance/AAMembershipModel';
import { MEMBERSHIP_ACTIVE_RATIFICATION_PARAM } from './consts';
import {
  IResendWelcomeEmailData,
  IResendWelcomeEmailModel,
} from '../../../../models/interfaces/ResendWelcomeEmailModel';
import { IUpdateAAMembershipOptionsModel } from '../../../../models/interfaces/Membership/AirAmbulance/UpdateAAMembershipOptionsModel';
import { ICreateRetailMembershipResultModel } from '../../../../models/interfaces/CreateRetailMembershipResultModel';
import { CreateAARetailMembershipModel } from '../../../../models/types/AirAmbulance/CreateAARetailMembershipModel';

export const createAAOrganizationMembership = (
  organizationId: string,
  subscriptionId: string,
  data: CreateMembershipModel,
): Promise<AxiosResponse<string>> =>
  axios.post(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}/memberships`,
    data,
    {
      baseURL: config.airAmbulanceApi.baseUrl,
    },
  );

export const createAARetailMembershipRequest = (
  data: CreateMembershipModel,
  setAlreadyPayed?: boolean,
  handlePayment?: boolean,
): Promise<AxiosResponse<ICreateRetailMembershipResultModel>> => {
  const queryParams: QueryParamItem[] = [
    {
      name: 'setAlreadyPayed',
      value: setAlreadyPayed,
    },
    {
      name: 'handlePayment',
      value: handlePayment,
    },
  ];
  const params = prepareQueryParams({ queryParams });

  return axios.post(`api/memberships${params}`, data, {
    baseURL: config.airAmbulanceApi.baseUrl,
  });
};

export const analyzeAACreateRetailMembership = (
  data: CreateMembershipModel,
): Promise<AxiosResponse> =>
  axios.post(`api/memberships/newmembershipanalysis`, data, {
    baseURL: config.airAmbulanceApi.baseUrl,
  });

export const getAAMembershipSummary = (
  organizationId: string,
  subscriptionId: string,
  membershipPlanId: string,
): Promise<AxiosResponse<IAddMembershipSummaryModel>> =>
  axios.get(
    `api/organizations/${organizationId}/subscriptions/${subscriptionId}/membershipPlans/${membershipPlanId}/summary`,
    {
      baseURL: config.airAmbulanceApi.baseUrl,
    },
  );

export const getDatesForNewAAMemberships = ({
  termType,
  subscriptionId,
  cancellationToken,
}: {
  termType?: string;
  subscriptionId?: string;
  cancellationToken?: AbortSignal;
}): Promise<AxiosResponse<IMembershipDates>> => {
  const queryParams: QueryParamItem[] = [
    {
      name: 'subscriptionId',
      value: subscriptionId,
    },
    {
      name: 'termType',
      value: termType,
    },
  ];
  const params = prepareQueryParams({ queryParams });
  return axios.get(`api/memberships/newmembershipdates${params}`, {
    baseURL: config.airAmbulanceApi.baseUrl,
    signal: cancellationToken,
  });
};

export const getAARetailMembershipPlanSummary = (
  membershipPlanId: string,
  promoCode: string,
  effectiveDate: string,
  autoRenewal?: boolean,
  cancellationToken?: AbortSignal,
): Promise<AxiosResponse<IAddMembershipSummaryModel>> => {
  const queryParams: QueryParamItem[] = [
    {
      name: 'membershipPlanId',
      value: membershipPlanId,
    },
    {
      name: 'promoCode',
      value: promoCode,
    },
    {
      name: 'effectiveDate',
      value: effectiveDate,
    },
    {
      name: 'autoRenewal',
      value: autoRenewal,
    },
  ];
  const params = prepareQueryParams({ queryParams });
  return axios.get(`api/memberships/summary${params}`, {
    baseURL: config.airAmbulanceApi.baseUrl,
    signal: cancellationToken,
  });
};

export const getAAExistingMembershipSummary = (
  membershipId: string,
): Promise<AxiosResponse<IAddMembershipSummaryModel>> =>
  axios.get(`api/memberships/${membershipId}/summary`, {
    baseURL: config.airAmbulanceApi.baseUrl,
  });

export const getAAMembershipsMembers = (
  organizationId: string,
  params?: string,
): Promise<AxiosResponse<IPageListModel<IMemberListItem>>> => {
  const queryParams: QueryParamItem[] = [
    { name: 'organizationId', value: organizationId },
  ];

  const currentParams = prepareQueryParams({
    initQueryString: params,
    queryParams,
  });

  return axios.get(`api/members${currentParams}`, {
    baseURL: config.airAmbulanceApi.baseUrl,
  });
};

export const addAASecondaryMemberRequest = (
  membershipId: string,
  data: ICreateSecondaryMemberModel,
): Promise<AxiosResponse> =>
  axios.post(`api/memberships/${membershipId}/members`, data, {
    baseURL: config.airAmbulanceApi.baseUrl,
  });

export const getAAMembershipDetails = (
  membershipId: string,
): Promise<AxiosResponse<IAAMembershipModel>> =>
  axios.get(`api/memberships/${membershipId}`, {
    baseURL: config.airAmbulanceApi.baseUrl,
  });

export const getAAMembershipRenewalSuggestion = (
  membershipId: string,
): Promise<AxiosResponse<CreateAARetailMembershipModel>> =>
  axios.get(`api/memberships/${membershipId}/renewalsuggestion`, {
    baseURL: config.airAmbulanceApi.baseUrl,
  });

export const postAAResendWelcomeEmail = (
  membershipId: string,
  memberId: string,
  data: IResendWelcomeEmailData,
  cancellationToken: AbortSignal,
): Promise<AxiosResponse<IResendWelcomeEmailModel>> =>
  axios.post(
    `api/memberships/${membershipId}/members/${memberId}/welcomeEmail`,
    data,
    {
      baseURL: config.airAmbulanceApi.baseUrl,
      signal: cancellationToken,
    },
  );

export const putAAMemberToBeRenewed = (
  membershipId: string,
  memberId: string,
  toBeRenewed: boolean,
): Promise<AxiosResponse> =>
  axios.put(
    `api/memberships/${membershipId}/members/${memberId}`,
    { toBeRenewed },
    {
      baseURL: config.airAmbulanceApi.baseUrl,
    },
  );

export const deleteAASecondaryMemberRequest = (
  membershipId: string,
  memberId: string,
  cancellationToken: AbortSignal,
): Promise<AxiosResponse<void>> =>
  axios.delete(`api/memberships/${membershipId}/secondarymember/${memberId}`, {
    baseURL: config.airAmbulanceApi.baseUrl,
    signal: cancellationToken,
  });

export const deleteAAMembershipRequest = (
  membershipId: string,
  isActive: boolean,
  cancellationToken: AbortSignal,
): Promise<AxiosResponse<void>> =>
  axios.delete(
    `api/memberships/${membershipId}${
      isActive ? MEMBERSHIP_ACTIVE_RATIFICATION_PARAM : ''
    }`,
    {
      baseURL: config.airAmbulanceApi.baseUrl,
      signal: cancellationToken,
    },
  );
export const updateAAMembershipOptionsRequest = (
  membershipId: string,
  options: IUpdateAAMembershipOptionsModel,
  cancellationToken: AbortSignal,
): Promise<AxiosResponse<IAAMembershipModel>> =>
  axios.put(`api/memberships/${membershipId}/options`, options, {
    baseURL: config.airAmbulanceApi.baseUrl,
    signal: cancellationToken,
  });
